import React from 'react';

import type {
  ShellContentProps,
  ShellFooterProps,
  ShellHeaderProps,
  ShellProps,
} from '../Shell/Shell';
import { shadows } from '../../common/shadows';
import { sizing } from '../../common/sizing';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { useViewport } from '../../utilities/useViewport';
import { Shell, ShellContent, ShellFooter, ShellHeader } from '../Shell/Shell';

const DrawerHeaderContainer = styled(ShellHeader);

export function DrawerHeader(props: ShellHeaderProps) {
  return <DrawerHeaderContainer {...props} />;
}

export type DrawerContentProps = ShellContentProps & { ref?: React.Ref<HTMLDivElement | null> };

export const DrawerContentContainer = styled(ShellContent);

export function DrawerContent({ children, ref, ...remaining }: DrawerContentProps) {
  return (
    <DrawerContentContainer ref={ref} {...remaining}>
      {children}
    </DrawerContentContainer>
  );
}

const DrawerFooterContainer = styled(ShellFooter);

export type DrawerFooterProps = ShellFooterProps & {
  /**
   * Provide primary actions for your drawer.
   */
  actions?: React.ReactNode;
  /**
   * Provide secondary actions, positioned at the start of the footer.
   */
  secondaryActions?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement | null>;
};

export function DrawerFooter({ actions, secondaryActions, ref, ...remaining }: DrawerFooterProps) {
  return <DrawerFooterContainer ref={ref} start={secondaryActions} end={actions} {...remaining} />;
}

// Guarantees a minimum internal width for the Drawer.
export const drawerWidth = sizing.primary * 2 + 320;

export const DrawerContainer = styled(Shell, {
  position: 'relative',
  zIndex: zIndexes.drawer,
  gridArea: 'drawer',
  overflow: 'hidden',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayAppLight,
  pointerEvents: 'auto',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayAppDark,
  },

  variants: {
    preset: {
      initial: {
        '@desktop': {
          width: `$${drawerWidth}`,
          minWidth: `$${drawerWidth}`,
        },
      },
      wide: {
        '@desktop': {
          width: `40vw`,
          minWidth: `40vw`,
        },
      },
      'extra-wide': {
        '@desktop': {
          width: `80vw`,
          minWidth: `80vw`,
        },
      },
    },
  },
});

export type DrawerProps = ShellProps & {
  preset?: 'initial' | 'wide' | 'extra-wide';
};

export function Drawer({
  children,
  contentMode = 'fit',
  preset = 'initial',
  width,
  maxWidth,
  minWidth,
  ref,
  ...remaining
}: DrawerProps) {
  const { breakpoint } = useViewport();
  return (
    <DrawerContainer
      ref={ref}
      contentMode={contentMode}
      preset={preset}
      width={breakpoint !== 'desktop' ? '100%' : width}
      maxWidth={breakpoint !== 'desktop' ? '100%' : maxWidth}
      minWidth={breakpoint !== 'desktop' ? '100%' : minWidth}
      {...remaining}
    >
      {children}
    </DrawerContainer>
  );
}
